import { type Component, createSignal, Show } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CvProceedDialog } from "@/domains/company/anonymization/CvProceedDialog";
import { AnonymizationUpsellModal } from "@/domains/company/anonymization/components/anonymization-upsell-modal";
import type { CvCandidate } from "@/domains/company/anonymization/types";
import { useAnonymousCvAccess } from "@/domains/company/anonymization/use-anonymization-access";
import { PremiumLockBadge } from "@/domains/company/talent-pool/components/premium-lock-badge";
import { useCvSelection } from "../hooks/useCvSelection";
import { useSearchCvDownload } from "../hooks/useSearchCvDownload";
import { useFreelanceSearchStore } from "../store/freelance-search-store";
import type { FreelanceSheetData } from "../types";

/**
 * CTA "CV anonimo" del dettaglio candidato — gemella di "Contatta".
 *
 * Alla PRIMA scelta il candidato non entra subito in selezione: un modale
 * chiede se scaricare subito o continuare a scegliere, e sono le sue due
 * strade a decidere. "Scarica subito" scarica quel solo PDF e finisce lì,
 * senza barra flottante; "aggiungi altri candidati" lo mette in selezione e
 * fa comparire la barra, dove i successivi si accodano (max 4) senza più
 * passare dal modale. Se il candidato è già in selezione la CTA diventa
 * "rimuovi", come fa "Contatta".
 *
 * Il download è sempre un solo file — un PDF per un candidato, uno ZIP per più
 * di uno (vedi `useCvDownload`): niente download multipli da sbloccare nel
 * browser.
 */
export const AnonymousCvButton: Component<{
  freelancer: FreelanceSheetData;
  onClose?: () => void;
  class?: string;
}> = (props) => {
  const { t } = useI18n();
  const access = useAnonymousCvAccess();
  const cvSelection = useCvSelection();
  const cvDownload = useSearchCvDownload();
  const [store] = useFreelanceSearchStore();

  /* Le due selezioni del dettaglio ("Contatta" e "CV anonimo") sono mutuamente
     esclusive: portarle avanti insieme significherebbe due barre flottanti, due
     tetti da 4 e due liste che la card dovrebbe colorare insieme. Finché una è
     aperta l'altra CTA è disabilitata, e il tooltip dice perché. */
  const blockedByContact = () => store.selectedFreelancers.length > 0;
  /* Il candidato in attesa di conferma: c'è solo mentre il modale della prima
     scelta è aperto. Finché sta qui NON è in selezione — così la barra
     flottante non spunta dietro al modale e chiuderlo senza scegliere non
     lascia dietro un candidato che l'utente non ha mai confermato. */
  const [pending, setPending] = createSignal<CvCandidate | null>(null);
  const [upsellOpen, setUpsellOpen] = createSignal(false);

  const candidate = (): CvCandidate => ({
    id: String(props.freelancer.id),
    full_name: props.freelancer.full_name ?? null,
    avatar_url: props.freelancer.avatar_url ?? null,
    job_title: props.freelancer.job_title ?? null,
  });

  const isSelected = () => cvSelection.isSelected(props.freelancer.id);

  const handleAdd = () => {
    if (blockedByContact()) return;
    // Locked: show what they'd get instead of building a selection they cannot
    // download. `inert` swallows the click — the entitlement is still in
    // flight and guessing either way would be wrong.
    if (access() === "locked") {
      setUpsellOpen(true);
      return;
    }
    if (access() === "inert") return;

    // Alla prima scelta l'aggiunta aspetta il modale; dopo, la barra è già
    // sullo schermo e il candidato entra subito in selezione.
    if (cvSelection.count() === 0) {
      setPending(candidate());
      return;
    }
    cvSelection.add(candidate());
    props.onClose?.();
  };

  /**
   * Scarica subito: un candidato solo, un PDF, nessuna barra. Il pendente NON
   * entra in selezione — chi sceglie questa strada ha finito, e una barra che
   * comparisse a download avviato chiederebbe di sparecchiare una selezione
   * che l'utente non ha mai chiesto di costruire. Si scarica quindi dal
   * candidato in mano, che non è (e non sarà) in `selected()`.
   *
   * A differenza di "Contatta" qui non si naviga, quindi lo sheet va chiuso a
   * mano: il download è un effetto laterale e lasciare aperto il dettaglio
   * sopra la lista nasconderebbe il toast.
   */
  const handleDownloadNow = () => {
    const confirmed = pending();
    if (!confirmed) return;
    setPending(null);
    cvDownload.download([confirmed.id]);
    props.onClose?.();
  };

  /**
   * Continua a selezionare: qui sì il pendente entra in selezione, ed è questo
   * il momento in cui la barra flottante compare in alto con il primo slot
   * pieno. Lo sheet si chiude per lasciare vedere la lista e la barra.
   */
  const handleSelectMore = () => {
    const confirmed = pending();
    if (confirmed) cvSelection.add(confirmed);
    setPending(null);
    props.onClose?.();
  };

  return (
    <Show when={access() !== "absent"}>
      <Show
        when={isSelected()}
        fallback={
          <Tooltip>
            {/* Il trigger avvolge un `span`, non il bottone: un `<button
                disabled>` non emette eventi di puntatore, quindi appeso
                direttamente al bottone il tooltip non comparirebbe proprio nel
                caso in cui serve spiegare il blocco. */}
            <TooltipTrigger as="span" class={props.class ?? "block w-full"}>
              <Button
                variant="outline"
                size="lg"
                class="w-full"
                onClick={handleAdd}
                disabled={
                  blockedByContact() ||
                  (cvSelection.isFull() && access() !== "locked")
                }
              >
                <Show when={access() === "locked"}>
                  <PremiumLockBadge class="mr-1.5" />
                </Show>
                {/* A selezione vuota la CTA promette l'azione ("Scarica CV
                    anonimo"), come fa "Contatta": "Aggiungi ai CV anonimi"
                    rimanderebbe a una lista che ancora non esiste. Da lì in poi
                    l'etichetta dice che si accoda. */}
                <span class="text-sm">
                  {cvSelection.count() === 0
                    ? t(
                        "company.search.freelancers.result.anonymous_cv.add_first",
                      )
                    : t(
                        "company.search.freelancers.result.anonymous_cv.add_more",
                      )}
                </span>
              </Button>
            </TooltipTrigger>
            <Show when={blockedByContact()}>
              <TooltipContent class="max-w-xs">
                {t(
                  "company.search.freelancers.result.anonymous_cv.blocked_by_contact",
                )}
              </TooltipContent>
            </Show>
          </Tooltip>
        }
      >
        <Button
          variant="destructiveOutline"
          size="lg"
          class={props.class ?? "w-full"}
          onClick={() => cvSelection.remove(props.freelancer.id)}
        >
          <span class="text-sm">
            {t("company.search.freelancers.result.anonymous_cv.remove")}
          </span>
        </Button>
      </Show>

      <AnonymizationUpsellModal
        open={upsellOpen()}
        onOpenChange={setUpsellOpen}
        feature="anonymous_cv"
        source="active_search_sheet"
      />

      {/* Chiuso senza scegliere (ESC, click fuori) il candidato pendente viene
          semplicemente buttato: niente selezione, niente barra. */}
      <Show when={pending()}>
        {(confirmed) => (
          <CvProceedDialog
            open
            onOpenChange={(open) => {
              if (!open) setPending(null);
            }}
            candidate={confirmed()}
            onDownloadNow={handleDownloadNow}
            onSelectMore={handleSelectMore}
          />
        )}
      </Show>
    </Show>
  );
};
